/**
 * The `travel` reach subsystem (plan R17, order 200; spec §4): cruise, trade lanes, interdiction, relay transit, nav
 * targets and routes, on top of R6's maths and R12's session. It owns the commands `setNavTarget`, `plotRoute`,
 * `clearRoute` and `jettison` (M8 TR-8b1: ends a tug; the cargo drop is the bridge's), provides no interactables, and exposes a control filter and the "Travel" tuning group.
 *
 * Per tick (`step`, after the session): the drive cooldown, then the mode's step (normal flight and the charge, cruise,
 * interdiction, lane ride or relay transit), then the nav distance. Allocation-free (D-014) except on rare events
 * (engaging, drops, spawns, lane entry and exit, system switches).
 *
 * Configs: the core's live `reach-travel.json` and `reach-streaming.json` objects when it has them (so the session,
 * the encounter port and the dev panel share one copy), else the bundled files; `options.configs` overrides both.
 */
import { SIM_DT } from '../../core/loop'
import type { SystemLayout } from '../../galaxy/layout/types'
import type { PlayerControl } from '../../input/playerInput'
import type { ReachCore, ReachSubsystem } from '../../reach/runtime/types'
import type { Sim } from '../../sim'
import { loadStreamingConfig } from '../../streaming'
import { Life } from '../../world/components'
import { loadTravelConfig, type TravelConfig } from '../config'
import { buildMassLockInput } from '../math'
import { loadTravelRuntimeConfig } from './config'
import { stepCruise, stepNormal } from './cruise'
import { travelControlFilter } from './filter'
import { endInterdictionJettisoned, stepInterdicted } from './interdiction'
import { stepLane } from './lanes'
import { clearRouteCommand, enterNav, plotRouteCommand, setNavTargetCommand, updateNav } from './nav'
import { createTravelRuntime, type TravelConfigs, type TravelRuntime } from './state'
import { stepTransit } from './transit'
import { createTravelTuning } from './tuning'

export const TRAVEL_ORDER = 200

export interface TravelSubsystemOptions {
  /** Config overrides (tests); otherwise the core's live configs, else the bundled JSON. */
  configs?: Partial<TravelConfigs>
}

export interface TravelSubsystem extends ReachSubsystem {
  /** The runtime's private state (tests and the debug API read it). */
  readonly runtime: TravelRuntime
}

/** The core's live configs, when it is R12's headless core (`ReachHeadlessCore.configs`). */
interface LiveConfigs {
  readonly configs?: { readonly travel?: TravelConfig; readonly streaming?: { transientDespawnM: number } }
}

/** Every travel state back to normal flight (a system switch that is not a relay transit, or a death). */
function resetMode(core: ReachCore): void {
  const t = core.travel
  t.mode = 'normal'
  t.chargeS = 0
  t.speed = 0
  t.allowed = 0
  t.etaS = 0
  t.laneIndex = -1
  t.laneExitRing = -1
  t.massLock = 'none'
  t.massLockIndex = -1
  const it = t.interdiction
  it.active = false
  it.escape = 0
  it.pull = 0
  it.timeLeftS = 0
  const tr = t.transit
  tr.phase = 'none'
  tr.t = 0
  tr.ready = false
  tr.destSystemIndex = -1
  t.relayHint.relayIndex = -1
}

export function createTravelSubsystem(options: TravelSubsystemOptions = {}): TravelSubsystem {
  const over = options.configs ?? {}
  const rt = createTravelRuntime({
    travel: over.travel ?? loadTravelConfig(),
    runtime: over.runtime ?? loadTravelRuntimeConfig(),
    streaming: over.streaming ?? loadStreamingConfig(),
  })

  return {
    name: 'travel',
    order: TRAVEL_ORDER,
    runtime: rt,

    init(core: ReachCore): void {
      const live = (core as ReachCore & LiveConfigs).configs
      if (over.travel === undefined && live?.travel !== undefined) rt.configs.travel = live.travel
      if (over.streaming === undefined && live?.streaming !== undefined) rt.configs.streaming = live.streaming
    },

    step(core: ReachCore, sim: Sim): void {
      const t = core.travel
      if (t.cooldownS > 0) {
        const left = t.cooldownS - SIM_DT
        t.cooldownS = left > 0 ? left : 0
      }
      const eid = sim.playerEid
      if (t.mode === 'transit') {
        stepTransit(rt, core, sim, eid)
        updateNav(rt, core, eid)
        return
      }
      if (eid < 0 || rt.layout === null || !core.session.ready || core.session.layout !== rt.layout) return
      if (Life.alive[eid] !== 1) {
        if (t.mode !== 'normal') resetMode(core)
        t.relayHint.relayIndex = -1
        return
      }
      switch (t.mode) {
        case 'normal':
        case 'charging':
          stepNormal(rt, core, sim, eid)
          break
        case 'cruise':
          stepCruise(rt, core, sim, eid)
          break
        case 'interdicted':
          stepInterdicted(rt, core, sim, eid)
          break
        case 'lane':
          stepLane(rt, core, sim, eid)
          break
      }
      updateNav(rt, core, eid)
    },

    onSystemEnter(core: ReachCore, sim: Sim, layout: SystemLayout): void {
      rt.layout = layout
      rt.massLock = buildMassLockInput(layout, rt.configs.runtime.massLockShipSlots, rt.configs.travel)
      rt.arrivedAt = sim.time
      rt.rollTimer = 0
      let rings = 0
      for (const lane of layout.lanes) if (lane.ringIndices.length > rings) rings = lane.ringIndices.length
      rt.laneUp.length = rings
      rt.laneUp.fill(false)
      core.travel.relayHint.relayIndex = -1
      enterNav(rt, core, core.travel.mode === 'transit')
    },

    onSystemLeave(core: ReachCore): void {
      if (core.travel.mode !== 'transit') resetMode(core)
      rt.layout = null
      rt.massLock = null
    },

    commands: {
      setNavTarget: (core, _sim, cmd) => setNavTargetCommand(rt, core, cmd),
      plotRoute: (core, _sim, cmd) => plotRouteCommand(rt, core, cmd),
      clearRoute: (core) => clearRouteCommand(rt, core),
      jettison: (core, sim) =>
        endInterdictionJettisoned(rt, core, sim)
          ? { ok: true, message: 'Cargo jettisoned: the interdiction is over' }
          : { ok: false, error: 'unavailable', message: 'Not interdicted: nothing to buy off' },
    },

    controlFilter: (core: ReachCore, c: PlayerControl): void => travelControlFilter(rt, core, c),

    tuning: createTravelTuning(() => rt.configs),
  }
}
